import { FC, useCallback, useEffect, useId, useState } from 'react';
import styled from 'styled-components';
import { u } from '../common/Units';
import BlockContainer from './BlockContainer';
import ChevronIcon from '../assets/chevron_right.svg?react';

type TextLayout = "default" | "openbox" | "closedbox";

interface ArticleTextProps {
  text: string,
  /** Box title; for "closedbox" it is always shown, since it is the toggle */
  title?: string,
  showTitle?: boolean,
  layout?: TextLayout
}

const HTMLElement = styled.div`
  font-weight: 400;
  line-height: 1.4em;
  font-size: 1.2em;

  & li {
    padding: 0 0 ${u(11)} 0;
  }

  & h2 {
    line-height: ${p => p.theme.scaleFactor * 2.1}rem;
  }
`;

const Box = styled.div`
  background-color: ${p => p.theme?.blockColor};
  border-radius: var(--block-radius);
  padding: ${u(22)} ${u(26)};

  &.closedbox {
    padding: 0;
  }

  & ${HTMLElement} > :first-child {
    margin-top: 0;
  }

  & ${HTMLElement} > :last-child {
    margin-bottom: 0;
  }
`;

const BoxTitle = styled.h2`
  font-size: 1.3em;
  line-height: 1.2em;
  margin: 0 0 ${u(15)};

  /* Title only (empty body text) */
  &:last-child {
    margin-bottom: 0;
  }
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${u(22)} ${u(26)};
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 1.3em;
  font-weight: 600;
  line-height: 1.2em;
  text-align: left;
  cursor: pointer;

  & svg {
    fill: currentColor;
    width: 1em;
    height: 1em;
    flex-shrink: 0;
    margin-left: ${u(15)};
    transform: rotate(90deg);
    transition: transform .2s;
  }

  &[aria-expanded="true"] svg {
    transform: rotate(-90deg);
  }
`;

/* 0fr -> 1fr animates height without measuring; older WebKit just snaps open */
const Collapsible = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows .25s ease;

  &.open {
    grid-template-rows: 1fr;
  }

  & > div {
    overflow: hidden;
    /* Keep collapsed text out of reach for touch and screen readers, after the close animation */
    visibility: hidden;
    transition: visibility 0s .25s;
  }

  &.open > div {
    visibility: visible;
    transition: none;
  }
`;

const CollapsibleInner = styled.div`
  padding: 0 ${u(26)} ${u(22)};
`;

/**
 * Render a HTML/markdown text block, optionally inside an open box or a collapsible box
 * @returns {JSX.Element} Component template
 */
 const BlockText: FC<ArticleTextProps> = ({text, title, showTitle, layout = "default"}) => {
  const [parsedString, setParsedString] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const contentId = useId();

  /**
   * Unescape html characters from string
   * @param {string} string String to parse
   * @returns {string} Parsed string
   */
   const unescapeHtml = useCallback((htmlString: string) => {
    if (typeof htmlString !== "string") { return ""; }
    return htmlString
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, "\"")
        .replace(/&#039;/g, "'");
  }, []);

  // Parse string as HTML or markdown
  useEffect(() => {
    if (typeof text !== "string") { return; }

      // HTML: Only include whitelisted tags
      const htmlStripRegex = /<(?!\/?(p|br|h1|h2|h3|h4|h5|h6|b|i|u|strong|em|strike|sub|sup|ul|ol|li|span)\b[\s]*[/]?)[^>]+>/gi;
      setParsedString(unescapeHtml(text).replace(htmlStripRegex, ""));
  }, [text, unescapeHtml]);

  const hasTitle = Boolean(title && title.trim().length > 0);
  // The editor leaves markup like "<p></p>" or "<p><br></p>" when the body text is emptied
  const hasText = parsedString.replace(/<[^>]*>/g, "").replace(/&nbsp;|\u00a0/g, " ").trim().length > 0;
  const content = hasText ? <HTMLElement dangerouslySetInnerHTML={{__html: parsedString}}/> : null;

  // A closed box needs a title to click on and text to open; without text, it is shown as a box with only the title
  if (layout === "closedbox" && hasTitle && hasText) {
    return (
      <BlockContainer className="textblock">
        <Box className="closedbox">
          <ToggleButton type="button" aria-expanded={isOpen} aria-controls={contentId} onClick={() => setIsOpen(open => !open)}>
            <span>{title}</span>
            <ChevronIcon aria-hidden/>
          </ToggleButton>
          <Collapsible id={contentId} className={isOpen ? "open" : undefined}>
            <div>
              <CollapsibleInner>{content}</CollapsibleInner>
            </div>
          </Collapsible>
        </Box>
      </BlockContainer>
    );
  }

  if (layout === "openbox" || layout === "closedbox") {
    // The title of a closed box is always shown, also when it has no text to open
    const titleVisible = hasTitle && (showTitle || layout === "closedbox");
    // Nothing to show: no empty box
    if (!titleVisible && !hasText) { return null; }

    return (
      <BlockContainer className="textblock">
        <Box>
          {titleVisible && (<BoxTitle>{title}</BoxTitle>)}
          {content}
        </Box>
      </BlockContainer>
    );
  }

  return (
    <BlockContainer className="textblock">{content}</BlockContainer>
  );
}

export default BlockText;
